import { Link } from 'react-router-dom';

import { useI18n } from '../i18n';
import { documentUrl, type Category, type Expense, type ExpenseStatus } from '../lib/api';
import { accentVar, cn } from '../lib/ui';
import { CategoryIcon, IconChevronRight, IconPaperclip, IconWarning } from './icons';
import { Badge, Spinner } from './ui';

export function categoryName(
  category: Pick<Category, 'nameEn' | 'nameZh'> | null | undefined,
  locale: string,
): string {
  if (!category) return '';
  return locale === 'zh-CN' ? category.nameZh : category.nameEn;
}

export function StatusBadge({ status }: { status: ExpenseStatus }) {
  const { t } = useI18n();
  const tone =
    status === 'reimbursed'
      ? 'positive'
      : status === 'partial'
        ? 'caution'
        : status === 'ineligible'
          ? 'neutral'
          : 'accent';

  return <Badge tone={tone}>{t(`status.${status}` as 'status.open')}</Badge>;
}

/** Thumbnail, or a placeholder that says what is happening instead. */
function DocumentThumb({ expense }: { expense: Expense }) {
  const { t } = useI18n();

  if (expense.previewDocumentId) {
    return (
      <img
        src={documentUrl(expense.previewDocumentId, 'thumb')}
        alt=""
        loading="lazy"
        className="h-12 w-12 shrink-0 rounded-[10px] border border-[var(--line)] object-cover"
      />
    );
  }

  if (expense.pendingDocumentCount > 0) {
    return (
      <span
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[10px] border border-[var(--line)] bg-[var(--surface-sunken)] text-[var(--ink-muted)]"
        title={t('detail.documentProcessing')}
      >
        <Spinner size={14} />
      </span>
    );
  }

  const color = accentVar(expense.categoryColor);
  return (
    <span
      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[10px]"
      style={{ background: `color-mix(in srgb, ${color} 14%, transparent)`, color }}
      aria-hidden="true"
    >
      <CategoryIcon glyph={expense.categoryIcon} size={22} />
    </span>
  );
}

export function ReceiptRow({ expense }: { expense: Expense }) {
  const { t, money, date, locale } = useI18n();

  const title =
    expense.provider ||
    expense.description ||
    categoryName(
      {
        nameEn: expense.categoryNameEn ?? '',
        nameZh: expense.categoryNameZh ?? '',
      },
      locale,
    ) ||
    t('common.uncategorized');

  return (
    <Link
      to={`/receipts/${expense.id}`}
      className="press flex items-center gap-3 px-4 py-3 hover:bg-[var(--surface-sunken)]"
    >
      <DocumentThumb expense={expense} />

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <span className="truncate text-[15px] font-medium text-[var(--ink)]">{title}</span>
          <span
            className={cn(
              'numeric shrink-0 text-[15px] font-semibold',
              expense.status === 'reimbursed' || expense.eligibility === 'ineligible'
                ? 'text-[var(--ink-muted)]'
                : 'text-[var(--ink)]',
            )}
          >
            {money(expense.amountCents)}
          </span>
        </div>

        <div className="mt-0.5 flex items-center gap-1.5 text-[13px] text-[var(--ink-secondary)]">
          {expense.personName ? (
            <>
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ background: accentVar(expense.personColor) }}
                aria-hidden="true"
              />
              <span className="truncate">{expense.personName}</span>
              <span aria-hidden="true">·</span>
            </>
          ) : null}
          <span className="shrink-0">{date(expense.serviceDate)}</span>

          {expense.documentCount === 0 && expense.pendingDocumentCount === 0 ? (
            <span
              className="ml-auto shrink-0 text-[var(--caution)]"
              title={t('detail.noDocuments')}
              aria-label={t('detail.noDocuments')}
            >
              <IconWarning size={15} />
            </span>
          ) : expense.documentCount > 1 ? (
            <span className="ml-auto flex shrink-0 items-center gap-0.5 text-[var(--ink-muted)]">
              <IconPaperclip size={13} />
              {expense.documentCount}
            </span>
          ) : null}
        </div>
      </div>

      <IconChevronRight size={18} className="shrink-0 text-[var(--ink-muted)]" />
    </Link>
  );
}

/**
 * Receipts grouped by month with a sticky heading, so scrolling through years
 * of history never loses its place in time.
 */
export function ReceiptGroups({ expenses }: { expenses: Expense[] }) {
  const { date, money } = useI18n();

  const groups = expenses.reduce<{ month: string; items: Expense[]; total: number }[]>(
    (accumulator, expense) => {
      const month = expense.serviceDate.slice(0, 7);
      const last = accumulator[accumulator.length - 1];
      if (last && last.month === month) {
        last.items.push(expense);
        last.total += expense.amountCents;
      } else {
        accumulator.push({ month, items: [expense], total: expense.amountCents });
      }
      return accumulator;
    },
    [],
  );

  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <section key={group.month}>
          {/*
            Pinned to the real bar height rather than a hardcoded 52px. On iOS
            the bar is taller by the status bar, so this used to stick *under*
            it and run into the first row of the group. Opaque for the same
            reason the bar is: translucent material let the rows ghost through
            the heading as they scrolled past.
          */}
          <div className="sticky top-[var(--header-height)] z-20 flex items-baseline justify-between bg-[var(--ground)] px-4 py-1.5 lg:px-8">
            <h3 className="text-[13px] font-semibold text-[var(--ink-secondary)]">
              {date(`${group.month}-01`, 'monthYear')}
            </h3>
            <span className="numeric text-[12px] text-[var(--ink-muted)]">
              {money(group.total, { round: true })}
            </span>
          </div>
          <div className="card mx-4 divide-y divide-[var(--line)] overflow-hidden lg:mx-8">
            {group.items.map((expense) => (
              <ReceiptRow key={expense.id} expense={expense} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
